import { supabase } from "./supabaseClient.js";
import { getCurrentProfile, isPasswordRecovery, signOut } from "./lib/auth.js";
import { escapeHtml, showError } from "./lib/ui.js";
import { renderLogin } from "./views/login.js";
import { renderResetPassword } from "./views/resetPassword.js";
import { renderDashboard } from "./views/dashboard.js";
import { renderCalendar } from "./views/calendar.js";
import { renderMovements } from "./views/movements.js";
import { renderProducts } from "./views/products.js";
import { renderPeople } from "./views/people.js";
import { renderInsights } from "./views/insights.js";
import { renderEmployeeEvent } from "./views/employeeEvent.js";
import { renderSeasons } from "./views/seasons.js";
import { renderReports } from "./views/reports.js";
import { renderMonthView } from "./views/monthView.js";
import { renderAudit } from "./views/audit.js";
import { fetchFlags } from "./lib/coloniaApi.js";
import { recargarSiHayNueva, vigilarVersion } from "./lib/version.js";

const app = document.querySelector("#app");

const ADMIN_ROUTES = [
  { path: "panel", label: "Panel general", render: renderDashboard },
  { path: "calendario", label: "Calendario", render: renderMonthView },
  { path: "eventos", label: "Lista de eventos", render: renderCalendar },
  { path: "colonia", label: "Colonia", render: renderSeasons },
  { path: "movimientos", label: "Ingresos y egresos", render: renderMovements },
  { path: "productos", label: "Productos y paquetes", render: renderProducts },
  { path: "alertas", label: "Alertas", render: renderInsights },
  { path: "reportes", label: "Reportes", render: renderReports },
  { path: "personas", label: "Personas", render: renderPeople },
  { path: "historial", label: "Historial de cambios", render: renderAudit },
];

// Se lee al arrancar, antes de que supabase-js consuma el hash de la URL.
const arrivedFromRecoveryEmail = isPasswordRecovery();
let recoveryHandled = false;

async function boot() {
  try {
    // Volver del correo de recuperación abre una sesión temporal: hay que
    // pedir la contraseña nueva antes de dejar entrar a la app.
    if (arrivedFromRecoveryEmail && !recoveryHandled) {
      recoveryHandled = true;
      renderResetPassword(app, boot);
      return;
    }

    const profile = await getCurrentProfile();

    if (!profile) {
      renderLogin(app, boot);
      return;
    }

    // "padres" ve las mismas pantallas que la familia, pero sin poder tocar
    // nada. Esconder los botones es comodidad: lo que de verdad lo impide es
    // la RLS, que no tiene ni una política de escritura para ese rol.
    if (profile.role === "admin" || profile.role === "padres") {
      // Interruptor de las secciones nuevas (Colonia nueva). Apagado o si no
      // se puede leer, la app se ve y anda exactamente como antes.
      const flags = await fetchFlags();
      renderAdminShell(profile, { coloniaNueva: flags.colonia, tanda2: flags.tanda2 });
    } else {
      renderEmployeeEvent(app, profile, boot);
    }
  } catch (error) {
    showError(app, error);
  }
}

function renderAdminShell(profile, { coloniaNueva = false, tanda2 = false } = {}) {
  // La marca va al <body>, no al shell: los modales se cuelgan del body, así
  // que una clase más abajo no los alcanzaría y quedaría un botón "Editar"
  // visible dentro de la ficha. Es una sola regla en vez de un `if` en cada
  // pantalla, y no se puede olvidar al agregar una nueva.
  const isReader = profile.role === "padres";
  document.body.classList.toggle("read-only", isReader);

  // "Papá y Mamá" no ve Personas: administrar accesos no es algo que un
  // observador necesite, y el desplegable de rol le quedaba clickeable aunque
  // la base rechazara el cambio. Al filtrar la ruta, entrar por la URL a mano
  // tampoco funciona: navigate() cae en la primera disponible.
  const routes = (
    profile.role === "padres"
      ? ADMIN_ROUTES.filter((r) => r.path !== "personas")
      : ADMIN_ROUTES
  ).map((r) =>
    // Con el interruptor prendido, Colonia es la sección nueva, y ahí
    // "Papá y Mamá" edita igual que admin (decisión del 14/10/2026). La
    // escritura la habilita la RLS de las tablas de colonia (puede_colonia);
    // en el resto de la app sigue siendo de sólo lectura.
    // Se carga recién al entrar: así el resto de la app no pesa más.
    coloniaNueva && r.path === "colonia"
      ? {
          ...r,
          editableForReaders: true,
          render: (main) =>
            import("./views/colonia/index.js")
              .then((m) => m.renderColonia(main))
              .catch((error) => showError(main, error)),
        }
      : r
  );

  // Empresa (tanda 2): el negocio completo, a la altura de Panel general.
  // Misma regla que Colonia: admin y Papá y Mamá, que también edita acá.
  if (tanda2) {
    routes.splice(1, 0, {
      path: "empresa",
      label: "Empresa",
      editableForReaders: true,
      render: (main) =>
        import("./views/empresa/index.js")
          .then((m) => m.renderEmpresa(main))
          .catch((error) => showError(main, error)),
    });
  }

  app.innerHTML = `
    <div class="app-shell">
      <div class="mobile-topbar">
        <h1>Grow Up</h1>
        <button id="menu-toggle">☰ Menú</button>
      </div>
      <aside class="sidebar" id="sidebar">
        <div class="sidebar-mobile-header">
          <strong>Menú</strong>
          <button id="menu-close" type="button" aria-label="Cerrar menú">✕</button>
        </div>
        <h1>Grow Up</h1>
        <nav id="nav">
          ${routes.map(
            (r) => `<button class="nav-link" data-route="${r.path}">${escapeHtml(r.label)}</button>`
          ).join("")}
        </nav>
        <div class="sidebar-footer">
          <div class="user-chip">${escapeHtml(profile.name)} · ${profile.role === "padres" ? "sólo mira" : "admin"}</div>
          <button class="btn secondary block" id="signout">Cerrar sesión</button>
        </div>
      </aside>
      <main class="main" id="main"></main>
    </div>
  `;

  const sidebar = app.querySelector("#sidebar");
  const main = app.querySelector("#main");

  app.querySelector("#menu-toggle").addEventListener("click", () => sidebar.classList.toggle("open"));
  // Sin esto, el menú a pantalla completa sólo se cierra eligiendo una sección.
  app.querySelector("#menu-close").addEventListener("click", () => sidebar.classList.remove("open"));

  app.querySelector("#signout").addEventListener("click", async () => {
    document.body.classList.remove("read-only");
    await signOut();
    boot();
  });

  app.querySelector("#nav").addEventListener("click", (e) => {
    const route = e.target.dataset?.route;
    if (route) window.location.hash = `#/${route}`;
  });

  const navigate = () => {
    // Nunca seguir con una versión vieja: si hay una nueva, recarga acá, al
    // cambiar de pantalla, donde no se pierde nada a medio cargar.
    if (recargarSiHayNueva()) return;
    const path = window.location.hash.replace(/^#\//, "") || "panel";
    const route = routes.find((r) => r.path === path) ?? routes[0];

    app.querySelectorAll(".nav-link").forEach((link) => {
      link.classList.toggle("active", link.dataset.route === route.path);
    });
    sidebar.classList.remove("open");
    document.body.classList.toggle("read-only", isReader && !route.editableForReaders);
    main.classList.remove("col-panel-t2");
    route.render(main);
  };

  window.addEventListener("hashchange", navigate);
  navigate();
}

supabase.auth.onAuthStateChange((event) => {
  if (event === "SIGNED_OUT") boot();

  // Supabase avisa cuando reconoce un token de recuperación. Cubre el caso de
  // tener la app ya abierta y hacer clic en el link del correo: ahí el
  // navegador sólo cambia el hash y no vuelve a ejecutar el arranque.
  if (event === "PASSWORD_RECOVERY") {
    recoveryHandled = true;
    renderResetPassword(app, boot);
  }
});

// PWA: instalable y capaz de abrir con mala conexión. El service worker sólo
// cachea el cascarón de la app, nunca respuestas de Supabase (ver public/sw.js).
if ("serviceWorker" in navigator && import.meta.env.PROD) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {
      // Sin service worker la app funciona igual, sólo pierde el modo offline.
    });
  });
}

vigilarVersion();
boot();
